import React, { useEffect, useRef, useState } from 'react'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@material-ui/core/Box'
import { Typography } from '@material-ui/core'
import { getCartTotals, getCurrentPaymentFlow, getTransactionResult, getInitializedValues, getPrintStatus } from '../Selectors'
import { APPLICATION_STRINGS } from '../../strings'

import { VNButton } from 'vn-react-components/VNComponents/VNButton'
import { VNTimeoutButton } from 'vn-react-components/VNComponents/VNTimeoutButton'
import { VNInputDialog } from 'vn-react-components/VNComponents/VNInputDialog'

import {
  bridge_updateTransactionLifecycle,
  CFDClearAppData,
  sendPrintReceipt,
  sendUserPaymentProgress,
} from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { resetCFD, setPrintStatus } from '../ActionCreators'
import { CFD_EVENTS, CFD_POS_PAYMENT_FLOWS, USER_PROGRESS } from '../Enums'
import { floatToDollars } from '../../VNUtil/Money'
import {
  setSnackbarSystemDataAlertSuccess,
  setSnackbarSystemDataAlertError,
} from 'vn-react-components/VNSnackbarSystem/ActionCreators'
import { emailValidation, VALID_EMAIL, VALID_SMS_NUMBER } from '../../VNUtil/Validator'
import { getAndroidSDKVersion } from '../../VNAndroidSDK/Selectors'
import { centsToDollars } from '../../utils/formatters'
import { VNProcessing } from './VNProcessing'
import QRCode from '../../components/QRCode'
import styles from './VNReceipt.module.scss'
import { generateReceiptQrUrl } from '../../components/receipt/ReceiptUtils'

const useStyles = makeStyles((theme) => ({
  printButtonContainer: {
    borderRadius: 5,
    backgroundColor: theme.custom.colors.greyMedium,
  },
}))

const PAYMENT_FLOW_TITLES = {
  GIFT_CARD: 'Gift Card',
  TICKET: 'Ticket',
  QR_PAY: 'Qr Pay',
}

export const VNReceipt = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const theme = useTheme()
  const timeoutRef = useRef()

  const transactionResult = useSelector((state) => getTransactionResult(state))
  const androidSDKVersion = useSelector((state) => getAndroidSDKVersion(state))
  const cartTotals = useSelector((state) => getCartTotals(state))
  const currentPaymentFlow = useSelector((state) => getCurrentPaymentFlow(state))

  const initialValues = useSelector((state) => getInitializedValues(state))

  const printStatus = useSelector((state) => getPrintStatus(state))

  // SELECTORS

  // LOCAL STATE
  const [openEmailDialog, setOpenEmailDialog] = useState(false)
  const [openTextDialog, setOpenTextDialog] = useState(false)
  const [printButtonClicked, setPrintButtonClicked] = useState(false)

  // USE EFFECTS

  useEffect(() => {
    return () => {
      dispatch(setPrintStatus({ message: '', error: false }))
      setPrintButtonClicked(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!printStatus?.get('message')) return

    setPrintButtonClicked(false)
    if (printStatus?.get('error') === 'true') {
      setSnackbarSystemDataAlertError(printStatus?.get('message'))
      return
    }

    setSnackbarSystemDataAlertSuccess(printStatus?.get('message'))
  }, [printStatus])
  /**
   * Validate the email(s) entered and displays error
   *
   * @param {String} value
   * @returns {Boolean}
   */
  const validEmail = (value) => {
    if (!value) {
      setSnackbarSystemDataAlertError('Please enter email')
      return false
    }
    if (!VALID_EMAIL.test(value)) {
      setSnackbarSystemDataAlertError('Invalid email(s)')
      return false
    }
    return true
  }

  /**
   * Validate the phone number entered and displays error
   *
   * @param {String} value
   * @returns {Boolean}
   */
  const validPhoneNumber = (value) => {
    if (!value) {
      setSnackbarSystemDataAlertError('Please enter phone number')
      return false
    }
    if (value.replace(/\D/g, '').length < 10) {
      setSnackbarSystemDataAlertError('Phone number too short')
      return false
    }
    if (!VALID_SMS_NUMBER.test(value)) {
      setSnackbarSystemDataAlertError('Invalid phone number')
      return false
    }
    return true
  }

  const onEmailDialogSave = async (inputVal) => {
    if (!validEmail(inputVal)) return
    let emailsToSend = inputVal.split(',')

    for (let i = 0; i < emailsToSend.length; i++) {
      const email = emailsToSend[i]
      const result = emailValidation(email)
      if (!result) {
        setSnackbarSystemDataAlertError("Can't send email. Incorrect email format.")
        return
      }
    }

    sendPrintReceipt(cartTotals.get('uuid'), emailsToSend, null)
    setOpenEmailDialog(false)

    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(false)
  }

  const onEmailDialogClose = () => {
    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(false)
    setOpenEmailDialog(false)
  }

  const onTextDialogSave = async (inputVal) => {
    if (!validPhoneNumber(inputVal)) return

    sendPrintReceipt(cartTotals.get('uuid'), null, inputVal)
    setOpenTextDialog(false)

    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(false)
  }

  const onTextDialogClose = () => {
    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(false)
    setOpenTextDialog(false)
  }

  // open the dialog on the screen
  const handleEmailReceiptClick = () => {
    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(true)
    setOpenEmailDialog(true)

    document.addEventListener('keyup', (event) => {
      const keyName = event.key

      if (keyName === 'Enter') {
        if (!validEmail(event.target.value)) return
        setOpenEmailDialog(false)
      }
    })
  }

  // open the dialog on the screen
  const handleTextReceiptClick = () => {
    timeoutRef.current.resetTimer()
    timeoutRef.current.isPaused(true)
    setOpenTextDialog(true)

    document.addEventListener('keyup', (event) => {
      const keyName = event.key

      if (keyName === 'Enter') {
        if (!validPhoneNumber(event.target.value)) return
        setOpenTextDialog(false)
      }
    })
  }

  // send the receipt to print
  const handlePrintClick = () => {
    // print the receipt - send through bus all the way to POS Web
    // to handle the print through the old bridge
    if (printButtonClicked) return

    timeoutRef.current.resetTimer()
    setPrintButtonClicked(true)
    sendPrintReceipt(cartTotals?.get('uuid'))
  }

  // handle the complete click
  const handleCompleteClick = () => {
    if (currentPaymentFlow === CFD_POS_PAYMENT_FLOWS.SPLIT_PAY) {
      bridge_updateTransactionLifecycle(CFD_EVENTS.CUSTOMER_DONE_WITH_RECEIPT)
    } else {
      sendUserPaymentProgress(USER_PROGRESS.CARD_PAYMENT.PAYMENT_FINISHED.TRANSACTION_COMPLETE)
    }

    // reset the CFD - only want to clear state locally
    dispatch(resetCFD(false, false, true))

    // clear accumulated android data
    CFDClearAppData(androidSDKVersion)
  }

  const displayPrintingButton = () => {
    if (initialValues.get('printingEnabled') === 'true') {
      return (
        <Box mt={1} className={classes.printButtonContainer}>
          <VNButton
            disableRipple={printButtonClicked}
            disabled={printButtonClicked}
            variant="contained"
            buttonVariant="button4"
            text={APPLICATION_STRINGS.PRINT_BUTTON_LONG_TEXT}
            onClick={handlePrintClick}
          />
        </Box>
      )
    }
  }

  const displayEmailButton = () => {
    const emailReceiptsEnabled = initialValues.get('emailReceiptsEnabled') === 'true'

    if (emailReceiptsEnabled && !transactionResult.has('offline')) {
      return (
        <Box pt={7}>
          <VNButton buttonVariant="button4" text={APPLICATION_STRINGS.EMAIL_BUTTON_LONG_TEXT} onClick={handleEmailReceiptClick} />
        </Box>
      )
    }
    return
  }

  const displayTextButton = () => {
    const textReceiptsEnabled = initialValues.get('textReceiptsEnabled') === 'true'
    if (textReceiptsEnabled && !transactionResult.has('offline')) {
      return (
        <Box pt={1}>
          <VNButton buttonVariant="button4" text={APPLICATION_STRINGS.TEXT_BUTTON_LONG_TEXT} onClick={handleTextReceiptClick} />
        </Box>
      )
    }
    return
  }

  const displayQRCode = () => {
    if (!initialValues.get('qrReceiptsUrl')) return
    if (initialValues.get('enablePosQrCodeReceipts') === 'true') {
      return (
        <div className={styles.qrContainer}>
          <Typography variant="h2">{APPLICATION_STRINGS.SCAN_DIGITAL_RECEIPT}</Typography>
          <div className={styles.qrCode}>
            <QRCode size={180} data={generateReceiptQrUrl(cartTotals?.get('uuid'), initialValues.get('qrReceiptsUrl'))}/>
          </div>
        </div>
      )
    }
    return
  }

  const display = () => {
    if (!transactionResult.isEmpty()) {
      return (
        <Box className={styles.receiptOverview}>
          <Typography variant="h2">Total Paid</Typography>
          <Typography style={theme.custom.large1}>${floatToDollars(transactionResult.get('amount'))}</Typography>
          <Box style={{ width: 480, margin: 'auto' }}>
            {displayEmailButton()}
            {displayTextButton()}
            {displayPrintingButton()}
            <Box pt={1}>
              <VNTimeoutButton
                ref={timeoutRef}
                buttonVariant="button4"
                countdownTime={10}
                text="I'm Done"
                fullWidth
                onClick={handleCompleteClick}
              />
            </Box>
            {currentPaymentFlow in PAYMENT_FLOW_TITLES && cartTotals?.get('remainingTicketBalance') !== undefined && (
              <Typography variant="h6" style={{ paddingTop: 7 }}>
                Remaining {PAYMENT_FLOW_TITLES[currentPaymentFlow]} Balance:{' '}
                {centsToDollars(cartTotals?.get('remainingTicketBalance'))}
              </Typography>
            )}
          </Box>
        </Box>
      )
    }
  }

  // eslint-disable-next-line no-unused-vars
  const displayDebug = () => {
    if (!cartTotals.isEmpty()) {
      return <div>{JSON.stringify(cartTotals)}</div>
    }
  }

  // prevent a blank screen from showing if transactionResult is not yet populated
  if (transactionResult?.isEmpty()) {
    return <VNProcessing />
  }

  return (
    <Box className={styles.root}>
      {/* {displayDebug()} */}
      <div className={styles.root}>
        {display()}
        {displayQRCode()}
      </div>
      <VNInputDialog
        open={openEmailDialog}
        title={'Enter email'}
        subTitle={'Separate multiple emails with commas'}
        onSave={onEmailDialogSave}
        onClose={onEmailDialogClose}
        type={'email'}
        endAdornment
        fullWidth
        width={'480px'}
      />
      <VNInputDialog
        open={openTextDialog}
        title={'Enter phone number'}
        onSave={onTextDialogSave}
        onClose={onTextDialogClose}
        type={'tel'}
        endAdornment
        fullWidth
        width={'480px'}
      />
    </Box>
  )
}
